import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { numberToWords } from "../../../lib/utils";
import client from "../../../clients";

/* ------------------------------------------------------------------ */
/* Module-level helpers (kept outside the component so they don't      */
/* remount on every render)                                            */
/* ------------------------------------------------------------------ */

const num = (v) => Number(v?.$numberDecimal ?? v ?? 0) || 0;
const money = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const SummaryRow = ({ label, value, bold, mb = "2px" }) => (
  <div style={{ display: "flex", marginBottom: mb }}>
    <div style={{ width: "280px", textAlign: "right", marginRight: "20px" }}>
      <p style={{ fontSize: "18px", fontWeight: bold ? 700 : undefined }}>{label}</p>
    </div>
    <div style={{ width: "100px", textAlign: "right" }}>
      <p style={{ fontSize: "18px", fontWeight: bold ? 700 : undefined }}>{value}</p>
    </div>
  </div>
);

const Cell = ({ w, head, children }) => (
  <p style={{ fontWeight: head ? 800 : 600, width: w, fontSize: "16px" }}>{children}</p>
);

export default function PaymentSlip(props) {
  const payment = props.payment;
  const printRef = useRef();
  const [isPooledMemberView, setPooledMemberView] = useState(true);

  const toggleSwitchPooledMemberView = () => setPooledMemberView(!isPooledMemberView);

  const handlePrint = useReactToPrint({
    documentTitle: `${payment?.invoice_number} ${
      payment?.member?.[0]?.userInfo?.name ? "- " + payment.member[0].userInfo.name : ""
    }`,
    content: () => printRef.current,
    pageStyle: `
      @page {
        margin: 0;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color: #000000 !important;
          opacity: 1 !important;
          font-family: 'Arial', sans-serif !important;
        }
        body {
          background: #ffffff !important;
          font-family: 'Arial', sans-serif !important;
        }
        #pool-member-display-switch {
          display: none !important;
        }
        ${!isPooledMemberView ? `#pool-member-view { display: none !important; }` : ""}
      }
    `,
  });

  if (!payment) return <></>;

  /* ---------------------------------------------------------------- */
  /* Derived data                                                      */
  /* ---------------------------------------------------------------- */

  const memberships = payment.membership || [];
  const firstMembership = memberships[0];
  const branch = payment.branch?.[0];
  const branchConfig = branch?.branchconfigs?.[0];
  const user = payment.user?.[0];
  const balancePayments = payment.balance_payments || [];
  const upgradedFrom = firstMembership?.upgraded_from_memberships?.[0];
  const hasProratedCredit = !!upgradedFrom;
  const proratedCredit = hasProratedCredit ? num(upgradedFrom?.billedInfo?.payableAmount) : 0;

  // GST-on-paid billed info only counts if it actually holds a payable amount
  const gstBilledInfo =
    payment.gstOnPaidAmountBilledInfo || firstMembership?.gstOnPaidAmountBilledInfo;
  const hasGstBilledInfo = num(gstBilledInfo?.payableAmount) > 0;

  const isGstOnPaid =
    (payment.gstOnPaidAmount === true || firstMembership?.gstOnPaidAmount === true) &&
    hasGstBilledInfo;

  const activeBilledInfo = isGstOnPaid ? gstBilledInfo : firstMembership?.billedInfo;

  const hasBalance = balancePayments.length > 0;

  // What was actually paid for one balance payment: billed info if present, else plain amount
  const balancePayable = (b) =>
    num(b?.gstOnPaidAmountBilledInfo?.payableAmount) > 0
      ? num(b.gstOnPaidAmountBilledInfo.payableAmount)
      : num(b?.amount);

  const parentPayable = num(activeBilledInfo?.payableAmount);
  const paidPayable = parentPayable + balancePayments.reduce((s, b) => s + balancePayable(b), 0);

  const getTotalPayment = () => {
    if (isGstOnPaid) return paidPayable;
    return num(payment.amount) + balancePayments.reduce((s, b) => s + balancePayable(b), 0);
  };

  // Outstanding balance: package-level wins, otherwise membership-level
  const pkgBalance = payment.package?.[0]?.payment_due_date
    ? num(payment.package[0].balance_amount)
    : firstMembership?.payment_due_date
    ? num(firstMembership.balance_amount)
    : null;

  const poolChargeTotal = isGstOnPaid
    ? num(activeBilledInfo?.poolCharge)
    : memberships.reduce((s, m) => s + num(m.billedInfo?.poolCharge), 0);

  const assignedTrainer =
    memberships.find((m) => m.trainer?.[0]?.name)?.trainer?.[0] ||
    firstMembership?.trainer?.[0] ||
    null;

  const getRoles = (profiles = []) => {
    let res = "";
    const role = firstMembership?.enquiry?.[0]?.closed_by_role || "";
    if (profiles?.length) {
      let roles = [];
      profiles.forEach((profile) => {
        if (profile?.roles?.length) roles = [...roles, ...(profile?.rolelist || [])];
      });
      if (role !== "") roles = roles.filter((d) => d.role === role);
      roles.sort((a, b) => a.role - b.role);
      res = roles?.[0]?.name ? client.config.roleNames[roles?.[0]?.name] : "";
    }
    return res;
  };

  const getPayableAmount = (p) => {
    const billedInfo = p.membership[0].billedInfo;
    const storedPayable = Number(billedInfo.payableAmount?.$numberDecimal || 0);
    if (storedPayable > 0) return storedPayable;

    const rackRate = Number(billedInfo.rackRate?.$numberDecimal || 0);
    const discount = Number(p.discount?.$numberDecimal || 0);
    const surcharge = Number(billedInfo.surCharges?.$numberDecimal || 0);
    const writeOff = p.isBalanceWriteOff ? num(p.writeOffAmount) : 0;
    const isInclusive = billedInfo.taxType !== "additional-tax";
    const totalTaxRate = p.membership[0].taxes.reduce((sum, t) => sum + Number(t.value), 0);

    let payable;
    if (!billedInfo.taxType || billedInfo.taxType === "") {
      payable = rackRate - discount - writeOff + surcharge;
    } else if (isInclusive) {
      payable = rackRate - discount - writeOff + surcharge;
    } else {
      const taxableAmount = rackRate - discount - writeOff;
      const taxAmount = (taxableAmount * totalTaxRate) / 100;
      payable = taxableAmount + taxAmount + surcharge;
    }
    return Math.round(payable);
  };

  /* ---------------------------------------------------------------- */
  /* GST-on-paid: tax rows + summary (used for parent AND each balance)*/
  /* ---------------------------------------------------------------- */

  const getGstTaxRows = (info) => {
    if (!info) return [];
    const allTaxes = firstMembership?.taxes || [];
    const ids = info.taxApplied || [];
    const list = ids.length ? allTaxes.filter((t) => ids.includes(t._id)) : allTaxes;
    const totalRate = list.reduce((s, t) => s + num(t.value), 0);
    const totalTax = num(info.totalTax);
    return list.map((t) => ({
      name: t.name,
      value: t.value,
      amount: totalRate ? (totalTax * num(t.value)) / totalRate : 0,
      isInclusive: info.taxType !== "additional-tax",
    }));
  };

  const renderGstSummary = (paidType, info, { isParent = false } = {}) => {
    if (!info) return null;
    const totalTax = num(info.totalTax);
    const surcharge = num(info.surCharges);
    const discount = num(info.discount);
    const payable = num(info.payableAmount);
    const gross = payable - totalTax - surcharge;
    const taxRows = getGstTaxRows(info);
    // const showTax = paidType == "balance-payment" && info?.gstOnPaidAmountBilledInfo && info?.gstOnPaidAmountBilledInfo?.taxApplied?.length > 0 ? true : taxRows.length > 0 && firstMembership?.billedInfo?.taxType !== "";
    const hasTax = num(info.totalTax) > 0 || info.taxApplied?.length > 0;
    const showTax = hasTax && taxRows.length > 0;

    return (
      <div>
        {isParent && (
          <SummaryRow
            label="Selected Plans Total:"
            value={`₹ ${money(num(info.rackRate ?? info.baseAmount))}`}
            bold
            mb="8px"
          />
        )}
        {isParent && poolChargeTotal > 0 && (
          <SummaryRow label="Pool Charge:" value={`+ ₹ ${money(poolChargeTotal)}`} bold mb="8px" />
        )}
        {isParent && payment.isBalanceWriteOff && (
          <SummaryRow
            label="Amount Written Off:"
            value={`- ₹ ${money(num(payment.writeOffAmount))}`}
            mb="4px"
          />
        )}
        {(isParent || discount > 0) && (
          <SummaryRow label="Total Discount:" value={`- ₹ ${money(discount)}`} />
        )}
        {showTax && (
          <SummaryRow
            mb="4px"
            label={payment.skipAggregateTax === true ? "Net Amount:" : "Gross Amount:"}
            value={`₹ ${gross.toFixed(2)}`}
          />
        )}
        {showTax &&
          taxRows.map(({ name, value, amount, isInclusive }) => (
            <SummaryRow
              key={name}
              mb="4px"
              label={`${name} (${value}%)${isInclusive ? " (Inclusive)" : ""}:`}
              value={`₹ ${amount.toFixed(2)}`}
            />
          ))}
        <SummaryRow label="Sub Total:" value={`₹ ${money(num(info.subTotal ?? payable))}`} />
        {surcharge > 0 && <SummaryRow label="Surcharge:" value={`₹ ${money(surcharge)}`} bold />}
        <SummaryRow label="Payable Amount:" value={`₹ ${money(payable)}`} bold />
        <SummaryRow label="Amount Paid:" value={`₹ ${money(payable)}`} />
        {isParent && pkgBalance !== null && (
          <SummaryRow label="Balance Amount:" value={`₹ ${money(pkgBalance)}`} />
        )}
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /* Legacy (non GST-on-paid) summary                                  */
  /* ---------------------------------------------------------------- */

  const renderLegacySummary = () => {
    const writeOff = num(payment.writeOffAmount);
    const discountTotal = num(payment.discount);
    const taxType = firstMembership?.billedInfo?.taxType;
    const showTax = taxType !== "" && firstMembership?.taxes?.length > 0;

    const taxable = memberships.reduce((total, m) => {
      const rackRate = num(m.billedInfo?.rackRate);
      const poolCharge = num(m.billedInfo?.poolCharge);
      const discount = discountTotal / memberships.length;
      const isInclusive = m.billedInfo?.taxType !== "additional-tax";
      const totalTaxRate = (m.taxes || []).reduce((s, t) => s + num(t.value), 0);
      const finalAmount = Math.max(0, rackRate + poolCharge - discount - proratedCredit) - writeOff;
      const base = isInclusive
        ? Math.floor(finalAmount / (1 + totalTaxRate / 100))
        : Math.floor(finalAmount);
      return total + base;
    }, 0);

    let taxRows = [];
    if (taxType !== "") {
      const taxMap = {};
      memberships.forEach((m) => {
        const rackRate = num(m.billedInfo?.rackRate);
        const poolCharge = num(m.billedInfo?.poolCharge);
        const discount = discountTotal / memberships.length;
        const isInclusive = m.billedInfo?.taxType !== "additional-tax";
        const totalTaxRate = (m.taxes || []).reduce((s, t) => s + num(t.value), 0);
        const writtenOff = payment.skipAggregateTax !== true ? writeOff : 0;
        const netAmount = Math.max(
          0,
          rackRate + poolCharge - discount - proratedCredit - writtenOff
        );

        (m.taxes || []).forEach((tax) => {
          const rate = num(tax.value);
          const taxAmount = isInclusive
            ? (((netAmount * totalTaxRate) / (100 + totalTaxRate)) * rate) / totalTaxRate
            : (netAmount * rate) / 100;

          if (taxMap[tax.name]) taxMap[tax.name].amount += taxAmount;
          else taxMap[tax.name] = { name: tax.name, value: tax.value, amount: taxAmount, isInclusive };
        });
      });
      taxRows = Object.values(taxMap);
    }

    const surchargeBase =
      num(firstMembership?.billedInfo?.baseAmount) ||
      num(firstMembership?.membershipplans?.[0]?.packages?.[0]?.rackRate);
    const surchargePct = surchargeBase
      ? Math.round((num(firstMembership?.billedInfo?.surCharges) / surchargeBase) * 100)
      : 0;

    const sumBilled = (key) => memberships.reduce((s, m) => s + num(m.billedInfo?.[key]), 0);

    return (
      <div>
        <SummaryRow
          label="Selected Plans Total:"
          value={`₹ ${money(
            payment.package?.[0]?.total_amount
              ? num(payment.package[0].total_amount)
              : num(firstMembership?.billedInfo?.rackRate)
          )}`}
          bold
          mb="8px"
        />
        {poolChargeTotal > 0 && (
          <SummaryRow label="Pool Charge:" value={`+ ₹ ${money(poolChargeTotal)}`} bold mb="8px" />
        )}
        {payment.isBalanceWriteOff && (
          <SummaryRow label="Amount Written Off:" value={`- ₹ ${money(writeOff)}`} mb="4px" />
        )}
        {hasProratedCredit && (
          <SummaryRow label="Prorated Credit:" value={`- ₹ ${money(proratedCredit)}`} mb="4px" />
        )}
        <SummaryRow label="Total Discount:" value={`- ₹ ${money(discountTotal)}`} />
        {showTax && <SummaryRow mb="4px" label="Taxable Amount:" value={`₹ ${money(taxable)}`} />}
        {taxRows.map(({ name, value, amount, isInclusive }) => (
          <SummaryRow
            key={name}
            mb="4px"
            label={`${name} (${value}%)${isInclusive ? " (Inclusive)" : ""}:`}
            value={`₹ ${amount.toFixed(2)}`}
          />
        ))}
        <SummaryRow label="Sub Total:" value={`₹ ${money(sumBilled("subTotal"))}`} />
        <SummaryRow
          label={`Surcharge(${surchargePct}%):`}
          value={`₹ ${money(sumBilled("surCharges"))}`}
          bold
        />
        <SummaryRow
          label="Payable Amount:"
          bold
          value={`₹ ${money(payment.isBalanceWriteOff ? getTotalPayment() : sumBilled("payableAmount"))}`}
        />
        <SummaryRow label="Amount Paid:" value={`₹ ${money(getTotalPayment())}`} />
        {pkgBalance !== null && (
          <SummaryRow label="Balance Amount:" value={`₹ ${money(pkgBalance)}`} />
        )}
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /* Shared pieces                                                     */
  /* ---------------------------------------------------------------- */

  const renderPaymentDetails = (wordsAmount) => {
    const fields = [
      ["Mobile Number/UPI Id", payment.upiId || user?.phone],
      // ["Aadhar Number", user?.aadhar_number],
      // ["PAN Number", user?.pan_number],
      // ["GSTIN Number", user?.gstin_number],
      ["Bank Name", payment.bankName],
      ["Source", payment.source],
      ["Card Type", payment.cardType],
      ["Cheque Number", payment.chequeNumber],
      ["Transaction ID", payment.transactionID],
      ["Payment Number", payment.paymentNumber],
      ["Approved Code", payment.approvedCode],
      ["Order ID", payment.razorPay?.razorpayOrderId],
      ["Approved ID", payment.razorPay?.razorpayPaymentId],
    ].filter(([, v]) => v);

    return (
      <div style={{ alignSelf: "flex-start", width: "500px" }}>
        <p style={{ fontSize: "18px", marginBottom: "14px" }}>
          Paid Amount (in words):{" "}
          <span style={{ fontWeight: "700", textTransform: "capitalize" }}>
            {numberToWords(wordsAmount)}
          </span>
        </p>
        {payment.mode && (
          <p style={{ fontSize: "18px" }}>
            <span style={{ fontWeight: "700" }}>Payment Mode:</span>{" "}
            {`${payment.mode}` === "Tez" ? "Google Pay" : payment.mode}
          </p>
        )}
        {fields.map(([label, v]) => (
          <p key={label} style={{ fontSize: "18px" }}>
            <span style={{ fontWeight: "700" }}>{label}:</span> {v}
          </p>
        ))}
      </div>
    );
  };

  const renderTerms = (className) =>
    branchConfig?.tc && (
      <>
        <div style={{ height: "1px", marginTop: "20px", marginBottom: "20px", background: "#e0e0e0" }} />
        <p style={{ fontWeight: "700", fontSize: "18px", marginBottom: "10px" }}>Terms & Conditions</p>
        <div className={className} style={{ fontSize: "13px", whiteSpace: "pre-line" }}>
          {branchConfig.tc}
        </div>
      </>
    );

  const renderSignatureFooter = () => (
    <div style={{ position: "absolute", bottom: 0, width: "86%" }}>
      <div style={{ justifyContent: "space-between", display: "flex" }}>
        <div style={{ alignSelf: "flex-start", width: "500px" }}>
          <p style={{ fontWeight: "700", fontSize: "18px" }}>Authorized By:</p>
          <p style={{ fontWeight: "700", fontSize: "18px", marginTop: "8px" }}>
            {firstMembership?.enquiry?.[0]?.closed_by_user?.[0]?.name} (
            {getRoles(firstMembership?.enquiry?.[0]?.closed_by_user?.[0]?.profiles)})
          </p>
        </div>
        <div style={{ alignSelf: "flex-start", width: "360px" }}>
          <div style={{ display: "flex" }}>
            <p style={{ fontWeight: "700", fontSize: "18px" }}>Member's Signature</p>
            <div
              style={{ width: "150px", height: "1px", background: "#000000", marginTop: "20px", marginLeft: "12px" }}
            />
          </div>
          <p style={{ fontStyle: "italic", marginTop: "8px", lineHeight: "18px" }}>
            *Note: This is computer generated invoice, signature is not required.
          </p>
        </div>
      </div>
    </div>
  );

  const pageStyle = {
    pageBreakAfter: "always",
    width: "auto",
    maxWidth: "297mm",
    minHeight: "400mm",
    padding: "20mm",
    background: "#ffffff",
    boxSizing: "border-box",
    position: "relative",
  };

  const isReceipt =
    num(firstMembership?.balance_amount) > 0 ||
    firstMembership?.billedInfo?.taxType === "" ||
    !firstMembership?.taxes?.length;

  const transferred = firstMembership?.transferred_by_user?.length > 0;
  const transferredFrom = firstMembership?.transferred_memberships?.[0];

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  return (
    <>
      <div className="global-modal-container payment-slip d-flex">
        <div
          className="flex-item margin-auto slideBottomToTop"
          style={{ width: window.innerWidth > 767 ? "auto" : "100%", height: "100%" }}
        >
          <div
            className="d-flex w-full space-between m-t-20 m-b-20"
            style={{
              padding: window.innerWidth < 768 ? "0 12px" : "0",
              gap: "12px",
              flexWrap: "nowrap",
            }}
          >
            <button
              className="flex-item"
              onClick={() => {
                printRef.current.style.overflowY = "visible";
                handlePrint();
              }}
              style={{
                padding: window.innerWidth < 768 ? "8px 12px" : "10px 20px",
                cursor: "pointer",
                fontSize: window.innerWidth < 768 ? "13px" : "14px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Print / Save as PDF
            </button>
            <div
              onClick={() => props.handleViewPaymentSlip()}
              className="d-flex flex-item close-payment-view-preview cursor-pointer"
              style={{ flexShrink: 0 }}
            >
              <svg
                className="flex-item margin-auto"
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.64648 16.4583L17.3548 0.75M17.3548 16.4583L1.64648 0.75"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Printable Area */}
          <div
            className="flex-item margin-auto"
            style={{
              overflow: "auto",
              borderRadius: "20px",
              height: "88%",
              width: window.innerWidth < 768 ? "94vw" : "auto",
              backgroundColor: "#ffffff",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div ref={printRef} style={{ height: "100%", margin: "0 auto", alignSelf: "center" }}>
              {/* ================= PAGE 1: PAYMENT LOGS ================= */}
              <div className="page" style={{ ...pageStyle, margin: "0 auto" }}>
                <p style={{ fontWeight: "700", fontSize: "18px" }}>Payment Logs</p>

                <div
                  style={{
                    display: "flex",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "12px",
                    marginTop: "20px",
                    marginBottom: "12px",
                  }}
                >
                  <Cell head w="20px">#</Cell>
                  <Cell head w="150px">Payment Date</Cell>
                  <Cell head w="150px">Amount Paid</Cell>
                  <Cell head w="200px">Payment Mode</Cell>
                  <Cell head w="300px">Invoice/Receipt No</Cell>
                  <Cell head w="200px">Received By</Cell>
                </div>

                <div style={{ display: "flex" }}>
                  <Cell w="20px">1</Cell>
                  <Cell w="150px">{payment.paymentDateString}</Cell>
                  <Cell w="150px">{money(isGstOnPaid ? parentPayable : num(payment.amount))}</Cell>
                  <Cell w="200px">{`${payment.mode}` === "Tez" ? "Google Pay" : payment.mode}</Cell>
                  <Cell w="300px">{payment.invoice_number}</Cell>
                  <Cell w="200px">{payment.received_by?.length ? payment.received_by[0].name : "-"}</Cell>
                </div>

                {balancePayments.map((balance, index) => (
                  <div key={balance._id || index} style={{ display: "flex" }}>
                    <Cell w="20px">{index + 2}</Cell>
                    <Cell w="150px">{balance.paymentDateString || balance.invoiceDateString}</Cell>
                    <Cell w="150px">{money(balancePayable(balance))}</Cell>
                    <Cell w="200px">{`${balance.mode}` === "Tez" ? "Google Pay" : balance.mode}</Cell>
                    <Cell w="300px">{balance?.invoice_number}</Cell>
                    <Cell w="200px">
                      {balance?.received_by?.length ? balance.received_by[0].name : "-"}
                    </Cell>
                  </div>
                ))}

                {renderTerms()}
                {renderSignatureFooter()}
              </div>

              {/* ================= PAGE 2: INVOICE / RECEIPT ================= */}
              <div className="page" style={pageStyle}>
                <div style={{ width: "100%", textAlign: "center" }}>
                  <p style={{ fontSize: "24px", fontWeight: "700" }}>
                    {isReceipt ? "PAYMENT RECEIPT" : "TAX INVOICE"}
                  </p>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "80px" }}>
                  <div style={{ alignSelf: "flex-start", width: "420px" }}>
                    {branch?.logoFileID && (
                      <img
                        style={{ width: "auto", height: "100px", marginBottom: "12px" }}
                        src={`${client.config.API_URL}/image/file/${branch.logoFileID}/auto/300/90?${Date.now()}`}
                        loading="lazy"
                        alt=""
                      />
                    )}
                    <p style={{ fontWeight: "700", fontSize: "22px", marginBottom: "20px" }}>{branch?.name}</p>

                    {branchConfig?.gstIn && (
                      <p style={{ fontSize: "18px", marginBottom: "4px" }}>
                        GSTIN: <span style={{ fontWeight: "700" }}>{branchConfig.gstIn}</span>
                      </p>
                    )}
                    {(branchConfig?.sac_code || branch?.sac_code) && (
                      <p style={{ fontSize: "18px", marginBottom: "4px" }}>
                        SAC Code:{" "}
                        <span style={{ fontWeight: "700" }}>{branchConfig?.sac_code || branch?.sac_code}</span>
                      </p>
                    )}
                    {branch?.address && (
                      <p style={{ fontSize: "18px", marginBottom: "4px" }}>
                        Address: <span style={{ fontWeight: "700" }}>{branch.address}</span>
                      </p>
                    )}
                    {branch?.phone && (
                      <p style={{ fontSize: "18px", marginBottom: "4px" }}>
                        Phone: <span style={{ fontWeight: "700" }}>{branch.phone}</span>
                      </p>
                    )}
                    {branch?.state && (
                      <p style={{ fontSize: "18px" }}>
                        Place Of Supply: <span style={{ fontWeight: "700" }}>{branch.state}</span>
                      </p>
                    )}

                    {assignedTrainer?.name && (
                      <div style={{ marginTop: "28px", borderRadius: "0 6px 6px 0" }}>
                        <p style={{ fontSize: "16px", color: "#2b2b2b" }}>Assigned Trainer</p>
                        <p style={{ fontWeight: "700", fontSize: "18px", marginTop: "2px" }}>
                          {assignedTrainer.name}
                          {assignedTrainer.phone ? (
                            <span style={{ fontWeight: "400" }}> ({assignedTrainer.phone})</span>
                          ) : null}
                        </p>
                      </div>
                    )}

                    {transferred ? (
                      <div style={{ marginBottom: "50px" }}>
                        <p style={{ fontWeight: "700", marginTop: "50px", fontSize: "18px", marginBottom: "4px" }}>
                          Transferred :
                        </p>
                        <p style={{ fontWeight: "normal", marginTop: "12px", fontSize: "16px", marginBottom: "4px" }}>
                          From:{" "}
                          <span style={{ fontWeight: "700" }}>
                            {firstMembership.transferred_by_user[0].name} ({firstMembership.transferred_by_user[0].phone})
                          </span>
                        </p>
                        <p style={{ fontWeight: "normal", marginTop: "8px", fontSize: "16px", marginBottom: "4px" }}>
                          To:{" "}
                          <span style={{ fontWeight: "700" }}>
                            {user?.name} ({user?.phone})
                          </span>
                        </p>
                        <p style={{ fontWeight: "normal", marginTop: "8px", fontSize: "16px", marginBottom: "4px" }}>
                          Sessions:{" "}
                          <span style={{ fontWeight: "700" }}>
                            {Number(transferredFrom?.completed_sessions || 0)} of{" "}
                            {Number(transferredFrom?.sessions || 0) + Number(transferredFrom?.add_on_days || 0)}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <>
                        <p style={{ fontWeight: "700", marginTop: "50px", fontSize: "18px", marginBottom: "4px" }}>
                          {user?.name}
                        </p>
                        {user?.email && (
                          <p style={{ fontSize: "18px", marginBottom: "4px" }}>{user.email}</p>
                        )}
                        <p style={{ fontSize: "18px", marginBottom: "4px" }}>{user?.phone}</p>
                        {user?.gstin_number && (
                          <p style={{ fontSize: "18px", marginBottom: "4px" }}>
                            <span style={{ fontWeight: "700" }}>GSTIN:</span> {user.gstin_number}
                          </p>
                        )}
                        {user?.aadhar_number && (
                          <p style={{ fontSize: "18px", marginBottom: "4px" }}>
                            <span style={{ fontWeight: "700" }}>Aadhar Number:</span> {user.aadhar_number}
                          </p>
                        )}
                        {user?.pan_number && (
                          <p style={{ fontSize: "18px", marginBottom: "4px" }}>
                            <span style={{ fontWeight: "700" }}>PAN Number:</span> {user.pan_number}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <div style={{ alignSelf: "flex-start", textAlign: "right" }}>
                    <p style={{ alignSelf: "flex-end", fontSize: "18px", fontWeight: "700" }}>
                      <span style={{ fontWeight: "700" }}>Date:</span> {payment.invoiceDateString}
                    </p>
                    <p style={{ alignSelf: "flex-end", fontSize: "18px", fontWeight: "700" }}>
                      Invoice No: {payment.invoice_number}
                    </p>
                  </div>
                </div>

                {/* Pooled members */}
                {firstMembership?.pooledMembers?.length > 0 && isPooledMemberView && (
                  <div id="pool-member-view" style={{ width: "100%", marginTop: "20px", marginBottom: "10px" }}>
                    <p style={{ fontWeight: "600", marginBottom: "8px", fontSize: "16px" }}>Pooled Members</p>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", width: "100%" }}>
                      {firstMembership.pooledMembers.map((pm, pmi) => (
                        <div
                          key={pm._id || pmi}
                          style={{
                            alignSelf: "flex-start",
                            marginRight: "16px",
                            paddingRight: "16px",
                            marginBottom: "12px",
                            borderRight:
                              firstMembership.pooledMembers.length !== pmi + 1 ? "1px solid #9a9a9a" : "",
                          }}
                        >
                          <p style={{ fontWeight: "700", fontSize: "14px" }}>Name: {pm.name}</p>
                          <p style={{ fontWeight: "700", fontSize: "14px" }}>Phone: {pm.phone}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {firstMembership?.pooledMembers?.length > 0 && (
                  <div
                    id="pool-member-display-switch"
                    className="d-flex"
                    style={{ marginBottom: "40px", marginTop: !isPooledMemberView ? "16px" : undefined }}
                  >
                    <p className="flex-item m-r-12 color-dark-blue font-weight-600" style={{ fontStyle: "italic" }}>
                      Show pooled member while printing/downloading bill
                    </p>
                    <div
                      onClick={toggleSwitchPooledMemberView}
                      className="toggle-button d-flex cursor-pointer"
                      style={{ backgroundColor: isPooledMemberView ? "#B3E28F" : undefined }}
                    >
                      <div
                        className="toggle-ball flex-item"
                        style={{
                          right: isPooledMemberView ? 0 : "auto",
                          left: !isPooledMemberView ? 0 : "auto",
                          backgroundColor: isPooledMemberView ? "#5d883c" : undefined,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Line items header */}
                <div
                  style={{
                    display: "flex",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "12px",
                    marginTop: "20px",
                    marginBottom: "12px",
                  }}
                >
                  <Cell head w="20px">#</Cell>
                  <Cell head w="300px">Description</Cell>
                  <Cell head w="120px">Duration</Cell>
                  <Cell head w="120px">Sessions</Cell>
                  <Cell head w="120px">Start Date</Cell>
                  <Cell head w="120px">End Date</Cell>
                  <Cell head w="120px">Cost</Cell>
                  <Cell head w="120px">Discount</Cell>
                </div>

                {/* Line items */}
                {memberships.map((m, index) => {
                  const pkg = m.membershipplans?.[0]?.packages?.find((d) => d.sessions == m.sessions);
                  const duration = pkg?.duration || m.duration || m.sessions;
                  const unitRaw = (pkg?.durationType || m.duration_unit || m.durationType || "").toLowerCase();
                  const unit = unitRaw.includes("month") ? "Month" : "Day";
                  const cost = num(m.billedInfo?.rackRate) || num(pkg?.rackRate);
                  const startsAt = m.upgraded_from_memberships?.length
                    ? m.upgraded_from_memberships[0]?.starts_at_string
                    : m.starts_at_string;

                  return (
                    <div key={m._id || index} style={{ display: "flex", marginBottom: "12px" }}>
                      <Cell w="20px">{index + 1}.</Cell>
                      <Cell w="300px">{m.membershipplans?.[0]?.name}</Cell>
                      <Cell w="120px">
                        {duration} {unit}
                      </Cell>
                      <Cell w="120px">{pkg?.sessions || m.sessions}</Cell>
                      <Cell w="120px">{startsAt}</Cell>
                      <Cell w="120px">{m.ends_at_string}</Cell>
                      <Cell w="120px">₹ {money(cost)}</Cell>
                      <Cell w="120px">₹ {money(num(m.billedInfo?.discount))}</Cell>
                    </div>
                  );
                })}

                {/* Payment details + summary */}
                <div style={{ justifyContent: "space-between", display: "flex", marginTop: "60px" }}>
                  {renderPaymentDetails(isGstOnPaid ? parentPayable : getTotalPayment())}
                  <div style={{ alignSelf: "flex-start" }}>
                    {isGstOnPaid
                      ? renderGstSummary("parent-payment", activeBilledInfo, { isParent: true })
                      : renderLegacySummary()}
                  </div>
                </div>

                {/* Balance payments (GST on paid amount) */}
                {isGstOnPaid && hasBalance && (
                  <div style={{ marginTop: "16px"}}>
                    {balancePayments.map((b, i) => {
                     const info = num(b.gstOnPaidAmountBilledInfo?.payableAmount) > 0 ? b.gstOnPaidAmountBilledInfo : null;
                      return (
                        <div key={b._id || i} style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px dashed #ccc"}}>
                          <div className="d-flex">
                            <p className="fs-16 m-r-16">Invoice Date: <span className="font-weight-700">{b.invoiceDateString}</span></p>
                            <p className="fs-16">Invoice No: <span className="font-weight-700">{b.invoice_number}</span></p>
                          </div>
                          <div className="m-t-12" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div style={{ alignSelf: "flex-start", width: "500px" }}>
                              <p style={{ fontSize: "18px", marginBottom: "14px" }}>
                                Paid Amount (in words):{" "}
                                <span style={{ fontWeight: "700", textTransform: "capitalize" }}>
                                  {numberToWords(balancePayable(b))}
                                </span>
                              </p>
                              {b.mode && (
                                <p style={{ fontSize: "18px" }}>
                                  <span style={{ fontWeight: "700" }}>Payment Mode:</span>{" "}
                                  {b.mode === "Tez" ? "Google Pay" : b.mode}
                                </p>
                              )}
                            </div>
                            <div style={{ alignSelf: "flex-start" }}>
                              {info ? (
                                renderGstSummary("balance-payment", info)
                              ) : (
                                <SummaryRow label="Amount Paid:" value={`₹ ${money(num(b.amount))}`} bold="700" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "40px", borderTop: "1px solid #e0e0e0", paddingTop: "12px"}}>
                      <p style={{ fontSize: "18px" }}>
                        Total Paid (in words):{" "}
                        <span style={{ fontWeight: "700", textTransform: "capitalize" }}>
                          {numberToWords(paidPayable)}
                        </span>
                      </p>
                    <div>
                      <SummaryRow label="Total Paid:" value={`₹ ${money(paidPayable)}`} bold />
                      {pkgBalance !== null && (
                        <SummaryRow label="Balance Amount:" value={`₹ ${money(pkgBalance)}`} />
                      )}
                      </div>
                    </div>
                  </div>
                )}

                {firstMembership?.comment && (
                  <p style={{ fontSize: "18px" }}>
                    <span style={{ fontWeight: "700" }}>Comment:</span> {firstMembership.comment}
                  </p>
                )}

                {renderTerms("m-b-8")}
                {renderSignatureFooter()}
              </div>

              {/* ================= PAGE 3: UPGRADE DETAILS ================= */}
              {hasProratedCredit && (
                <div className="page" style={pageStyle}>
                  <div style={{ height: "1px", marginTop: "40px", marginBottom: "20px", background: "#e0e0e0" }} />
                  <p style={{ fontWeight: "700", fontSize: "26px", marginBottom: "30px" }}>UPGRADED FROM:</p>
                  <p style={{ fontSize: "16px", marginBottom: "30px" }}>
                    <span style={{ fontWeight: "700", marginRight: "8px" }}>
                      {upgradedFrom.membershipplans?.[0]?.name} Invoice No:
                    </span>
                    {upgradedFrom.payments?.[0]?.invoice_number}
                    <span style={{ fontWeight: "700", margin: "0 0 0 20px", marginRight: "8px" }}>Date:</span>
                    {upgradedFrom.payments?.[0]?.invoiceDateString || upgradedFrom.starts_at_string}
                  </p>

                  <div style={{ display: "flex", justifyContent: "space-between", width: "550px", marginBottom: "12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "240px" }}>
                      <p style={{ fontSize: "16px" }}>Duration:</p>
                      <p style={{ fontSize: "16px", fontWeight: "700" }}>
                        {upgradedFrom.membershipplans?.[0]?.packages?.[0]?.durationType}
                      </p>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "240px" }}>
                      <p style={{ fontSize: "16px" }}>Payable amount:</p>
                      <p style={{ fontSize: "16px", fontWeight: "700" }}>
                        ₹{payment.isBalanceWriteOff ? getTotalPayment() : getPayableAmount(payment)}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", width: "550px", marginBottom: "12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "240px" }}>
                      <p style={{ fontSize: "16px" }}>Start date:</p>
                      <p style={{ fontSize: "16px", fontWeight: "700" }}>{upgradedFrom?.starts_at_string}</p>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "240px" }}>
                      <p style={{ fontSize: "16px" }}>End date:</p>
                      <p style={{ fontSize: "16px", fontWeight: "700" }}>{upgradedFrom?.ends_at_string}</p>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", width: "240px" }}>
                    <p style={{ fontSize: "16px" }}>Session:</p>
                    <p style={{ fontSize: "16px", fontWeight: "700" }}>
                      {Number(upgradedFrom.completed_sessions || 0)}/
                      {Number(upgradedFrom.sessions || 0) + Number(upgradedFrom?.add_on_days || 0)}
                    </p>
                  </div>

                  {renderSignatureFooter()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}